import React from "react";
import { graphql, Link } from "gatsby";

import {
    fitsPage,
    pageShell,
    figureHero,
    heroCopy,
    heroLead,
    primaryCta,
    heroMeta,
    heroVisual,
    sectionHeader,
    sectionEyebrow,
    figureGrid,
    figureCard,
    figureCardHeader,
    figureCardBody,
    figureVisual,
    figureIcon,
    figureImage,
    figureContent,
    figureAbout,
    figureStyles,
    figureStyleList,
    cardCta,

    measureSection,
    measureVisual,
    measureContent,
    measureSteps,
    measureStep,
    stepNumber,
    measureChecklist,
    measureGuideLink,
    styleTipsGrid,
    styleTip,
    styleTipIcon,
    inspirationGrid,
    inspirationCard,
    inspirationImage,
    finalCta,
    finalCtaContent,
    finalCtaButton,
} from "./what-fits-you.module.scss";

import hourglassIcon from "../../assets/images/what-fits/icons/figure-type-hourglass.png";
import pearIcon from "../../assets/images/what-fits/icons/figure-type-pear.png";
import appleIcon from "../../assets/images/what-fits/icons/figure-type-apple.png";
import rectangleIcon from "../../assets/images/what-fits/icons/figure-type-rectangle.png";
import invertedTriangleIcon from "../../assets/images/what-fits/icons/figure-type-inverted-triangle.png";
import { IPageBase } from "../../models/page-base.model";
import { ISite } from "../../models/site.model";
import usePageMetadata from "../../hooks/use-page-metadata";
import { RESULT_DATA } from "../../utils/figure-analysis";
import checkIcon from "../../assets/icons/check.svg";

// Tymczasowe kadry z zatwierdzonej makiety; docelowo zastąpią je osobne obrazy źródłowe.
import heroImage from "../../assets/images/what-fits/reference/figure-hero-reference.jpg";
import hourglassImage from "../../assets/images/what-fits/reference/figure-pear-reference.jpg";
import pearImage from "../../assets/images/what-fits/reference/figure-pear-20260722.jpg";
import appleImage from "../../assets/images/what-fits/reference/figure-apple-reference.jpg";
import rectangleImage from "../../assets/images/what-fits/reference/figure-rectangle-reference.jpg";
import triangleImage from "../../assets/images/what-fits/reference/figure-inverted-triangle-reference.jpg";
import measureImage from "../../assets/images/what-fits/reference/figure-measure-reference.jpg";
import inspirationOne from "../../assets/images/what-fits/reference/figure-gallery-1-reference.jpg";
import inspirationTwo from "../../assets/images/what-fits/reference/figure-gallery-2-reference.jpg";
import inspirationThree from "../../assets/images/what-fits/reference/figure-gallery-3-reference.jpg";
import inspirationFour from "../../assets/images/what-fits/reference/figure-gallery-4-reference.jpg";
import inspirationFive from "../../assets/images/what-fits/reference/figure-gallery-5-reference.jpg";
import inspirationSix from "../../assets/images/what-fits/reference/figure-gallery-6-reference.jpg";
import styleTipEmphasizeAssets from "../../assets/images/what-fits/style-tips/style-tip-emphasize-assets.png";
import styleTipBalance from "../../assets/images/what-fits/style-tips/style-tip-balance.png";
import styleTipFits from "../../assets/images/what-fits/style-tips/style-tip-fits.png";
import styleTipColorsPatterns from "../../assets/images/what-fits/style-tips/style-tip-colors-patterns.png";
import styleTipAccessories from "../../assets/images/what-fits/style-tips/style-tip-accessories.png";
import styleTipConfidence from "../../assets/images/what-fits/style-tips/style-tip-confidence.png";

import MainLayout from "../../layouts/main-layout";
import StaticPageNav from "../organisms/static-page-nav";
import MinimalFooter from "../organisms/minimal-footer";

interface IFitsProps extends IPageBase {
    readonly data: { site: ISite };
}

const figureResultData: Record<string, any> = RESULT_DATA;

const figureTypes = [
    {
        name: "KLEPSYDRA",
        icon: hourglassIcon,
        image: hourglassImage,
        about: figureResultData.hourglass.summary,
        styles: figureResultData.hourglass.bestFits,
    },
    {
        name: "GRUSZKA",
        icon: pearIcon,
        image: pearImage,
        about: figureResultData.pear.summary,
        styles: figureResultData.pear.bestFits,
    },
    {
        name: "JABŁKO",
        icon: appleIcon,
        image: appleImage,
        about: figureResultData.apple.summary,
        styles: figureResultData.apple.bestFits,
    },
    {
        name: "PROSTOKĄT",
        icon: rectangleIcon,
        image: rectangleImage,
        about: figureResultData.rectangle.summary,
        styles: figureResultData.rectangle.bestFits,
    },
    {
        name: "ODWRÓCONY TRÓJKĄT",
        icon: invertedTriangleIcon,
        image: triangleImage,
        about: figureResultData["inverted-triangle"].summary,
        styles: figureResultData["inverted-triangle"].bestFits,
    },
];

const styleTips = [
    { image: styleTipEmphasizeAssets, title: "Podkreślaj atuty", text: "Eksponuj to, co lubisz w swojej sylwetce." },
    { image: styleTipBalance, title: "Zachowaj równowagę", text: "Równoważ proporcje góry i dołu stylizacji." },
    { image: styleTipFits, title: "Dobieraj fasony", text: "Wybieraj kroje, które pracują dla Twojej figury." },
    { image: styleTipColorsPatterns, title: "Kolory i wzory", text: "Ciemniejsze kolory wyszczuplają, jasne dodają objętości." },
    { image: styleTipAccessories, title: "Dodatki", text: "Paski, biżuteria i buty potrafią zmienić proporcje." },
    { image: styleTipConfidence, title: "Pewność siebie", text: "Najlepszym dodatkiem jest Twoja pewność siebie." },
];

const inspirationImages = [
    inspirationOne,
    inspirationTwo,
    inspirationThree,
    inspirationFour,
    inspirationFive,
    inspirationSix,
];

const WhatFitsYou: React.FC<IFitsProps> = ({ data, pageContext }) => {
    const { pageTitle, defaultStructuredData } = usePageMetadata(pageContext, data.site);

    return (
        <MainLayout
            className={fitsPage}
            HeaderComponent={null}
            footer={false}
            SEOProps={{ title: pageTitle, structuredData: defaultStructuredData }}
        >
            <StaticPageNav menuId="what-fits-mobile-menu" />

            <div className={pageShell}>
                <section className={figureHero} aria-labelledby="figure-page-title">
                    <div className={heroCopy}>
                        <p className={sectionEyebrow}>TWÓJ STYL ZACZYNA SIĘ OD PROPORCJI</p>
                        <h1 id="figure-page-title">
                            Określ swoją figurę <span>i znajdź swój styl</span>
                        </h1>
                        <p className={heroLead}>
                            Każda sylwetka jest wyjątkowa. Poznaj jej atuty i dowiedz się, jak je
                            podkreślić idealnie dobraną modą.
                        </p>
                        <Link className={primaryCta} to="/quiz-figury/">
                            ROZPOCZNIJ ANALIZĘ FIGURY <span aria-hidden="true">→</span>
                        </Link>
                        <div className={heroMeta} aria-label="Zalety analizy">
                            {["Szybko", "Prosto", "Dla Ciebie"].map(item => (
                                <span key={item}>
                                    <img src={checkIcon} alt="" aria-hidden="true" />
                                    {item}
                                </span>
                            ))}
                        </div>
                    </div>
                    <div className={heroVisual}>
                        <img src={heroImage} alt="Kobiety o różnych typach sylwetki" />
                    </div>
                </section>

                <section aria-labelledby="figure-types-title">
                    <header className={sectionHeader}>
                        <p className={sectionEyebrow}>POZNAJ SWOJE PROPORCJE</p>
                        <h2 id="figure-types-title">Typy figur kobiecych</h2>
                        <p>Poznaj swój typ figury i zobacz, jakie fasony najlepiej podkreślą Twoje atuty.</p>
                    </header>
                    <div className={figureGrid}>
                        {figureTypes.map(item => (
                            <article className={figureCard} key={item.name}>
                                <header className={figureCardHeader}>
                                    <div className={figureIcon} aria-hidden="true">
                                        <img src={item.icon} alt="" />
                                    </div>
                                    <h3>{item.name}</h3>
                                </header>
                                <div className={figureCardBody}>
                                    <div className={figureVisual}>
                                        <img className={figureImage} src={item.image} alt={`Przykład sylwetki: ${item.name.toLowerCase()}`} />
                                    </div>
                                    <div className={figureContent}>
                                        <section className={figureAbout}>
                                            <h4>O figurze</h4>
                                            <p>{item.about}</p>
                                        </section>
                                        <section className={figureStyles}>
                                            <h4>Stylizacje, które pokochasz</h4>
                                            <ul className={figureStyleList}>
                                                {item.styles.map((style: string) => <li key={style}>{style}</li>)}
                                            </ul>
                                        </section>
                                        <Link className={cardCta} to="/quiz-figury/">SPRAWDŹ SWOJĄ FIGURĘ</Link>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                </section>

                <section className={measureSection} aria-labelledby="measure-title">
                    <div className={measureVisual}>
                        <img src={measureImage} alt="Pomiar sylwetki centymetrem krawieckim" />
                    </div>
                    <div className={measureContent}>
                        <p className={sectionEyebrow}>CZTERY PROSTE KROKI</p>
                        <h2 id="measure-title">Jak zmierzyć się w domu?</h2>
                        <p>Wykonaj kilka prostych pomiarów i porównaj swoje proporcje.</p>
                        <div className={measureSteps}>
                            {[
                                ["Zmierz", "Obwód biustu, talii, bioder i ramion."],
                                ["Porównaj", "Sprawdź, która część ciała jest najszersza."],
                                ["Określ typ", "Dopasuj proporcje do jednego z typów figury."],
                                ["Dobierz styl", "Wybierz fasony, które podkreślą Twoje atuty."],
                            ].map((step, index) => (
                                <article className={measureStep} key={step[0]}>
                                    <span className={stepNumber}>{index + 1}</span>
                                    <h3>{step[0]}</h3><p>{step[1]}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                    <aside className={measureChecklist} aria-label="Co zmierzyć">
                        <h3>Co zmierzyć?</h3>
                        <ul>
                            <li><strong>Obwód biustu</strong><span>W najszerszym miejscu</span></li>
                            <li><strong>Obwód talii</strong><span>W najwęższym miejscu</span></li>
                            <li><strong>Obwód bioder</strong><span>W najszerszym miejscu</span></li>
                            <li><strong>Szerokość ramion</strong><span>Od końca do końca ramion</span></li>
                        </ul>
                        <p>Pamiętaj: stań prosto, nie wciągaj brzucha i użyj miękkiej miarki krawieckiej.</p>
                        <Link className={measureGuideLink} to="/jak-prawidlowo-sie-mierzyc/">
                            Zobacz, jak prawidłowo się mierzyć <span aria-hidden="true">→</span>
                        </Link>
                    </aside>
                </section>

                <section aria-labelledby="style-tips-title">
                    <header className={sectionHeader}>
                        <p className={sectionEyebrow}>PROSTE ZASADY</p>
                        <h2 id="style-tips-title">Jak dobrać stylizację do swojej figury?</h2>
                        <p>Kilka zasad, które zawsze działają.</p>
                    </header>
                    <div className={styleTipsGrid}>
                        {styleTips.map(tip => (
                            <article className={styleTip} key={tip.title}>
                                <span className={styleTipIcon} aria-hidden="true">
                                    <img src={tip.image} alt="" />
                                </span>
                                <h3>{tip.title}</h3><p>{tip.text}</p>
                            </article>
                        ))}
                    </div>
                </section>

                <section aria-labelledby="inspiration-title">
                    <header className={sectionHeader}>
                        <p className={sectionEyebrow}>GOTOWE POMYSŁY</p>
                        <h2 id="inspiration-title">Inspiracje dla każdej figury</h2>
                        <p>Stylizacje dopasowane do Twoich proporcji.</p>
                    </header>
                    <div className={inspirationGrid}>
                        {inspirationImages.map((image, index) => (
                            <div className={inspirationCard} key={image}>
                                <img className={inspirationImage} src={image} alt={`Inspiracja stylizacyjna ${index + 1}`} />
                            </div>
                        ))}
                    </div>
                </section>

                <section className={finalCta} aria-label="Rozpocznij analizę figury">
                    <div className={finalCtaContent}>
                        <p>✦ TWÓJ STYL, TWOJE PROPORCJE</p>
                        <h2>Odkryj swój styl z Fashion Fairy</h2>
                        <span>Wirtualna stylistka pomoże Ci dobrać fasony idealne dla Twojej figury.</span>
                    </div>
                    <Link className={finalCtaButton} to="/quiz-figury/">ROZPOCZNIJ ANALIZĘ FIGURY →</Link>
                </section>
            </div>

            <MinimalFooter />
        </MainLayout>
    );
};

export const query = graphql`
    query($language: String!) {
        locales: allLocale(filter: { language: { eq: $language } }) {
            edges { node { ns data language } }
        }
        site { ...siteFields }
    }
`;

export default WhatFitsYou;
